import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Overlay, Popover } from 'react-bootstrap';
import {
  AVAILABILITY_STATE,
  CLOSED_NIGHT_LINE,
  availabilityView,
  isClosedState
} from '../../utils/availabilityUiHelpers';
import {
  formatDate,
  formatDateWithDay,
  formatDayOfMonthLatn,
  formatShortDate,
  formatWeekdayName
} from '../../utils/dateFormatter';
import { formatSARCompact } from '../../utils/currencyFormatter';
import { formatCount } from '../../utils/numberFormatter';
import { getPlatformColor, getPlatformDisplayName } from '../../utils/platformUtils';
import { GRID_TEXT, UNIT_STATUS_TAG, bedroomsLabel, nightsLabel, offersText } from '../../config/availabilityGridLabels';
import BookingRefLine from '../BookingRefLine';
import { promotionName } from '../pricing/airbnbOfferText';
import { percent } from '../pricing/pricingFormat';
import NightAirbnbOffers from './NightAirbnbOffers';

/**
 * The matrix of «التقويم»: one row per unit, one column per night, live bookings as
 * bars across their nights. READ-ONLY — every click leads to the page where the work is
 * already done: a night opens the unit calendar on that date, a bar opens the booking.
 *
 * Night state comes in pre-resolved (`unit.states`, from the shared resolver) and is painted
 * with AVAILABILITY_VIEW, so a night looks the same here as in the unit calendar.
 */

const { AVAILABLE, BOOKED, PARTIAL, UNKNOWN } = AVAILABILITY_STATE;
const WEEKEND = new Set([5, 6]); // Friday, Saturday — the Saudi weekend (0 = Sunday)
const RELOCATION_HOLD = 'relocation_guest_in_house';
const POP_TITLE_ID = 'ag-pop-title';

const hasPrice = (rate) => typeof rate === 'number' && rate > 0;
// The cell shows the same figure the popover prints, without the currency (column too narrow).
const priceFigure = (rate) => formatSARCompact(rate).replace(/\s*ر\.س$/, '');
const unitTitle = (unit) => (unit.unitNumber ? `${unit.unitNumber} · ${unit.name}` : unit.name);
const platformName = (platform) => getPlatformDisplayName(platform) || GRID_TEXT.platformFallback;
const runningPromotions = (unit) =>
  (unit.airbnbOffers?.status === 'ok' && Array.isArray(unit.airbnbOffers.runningPromotions) ? unit.airbnbOffers.runningPromotions : []);
// A tooltip is plain text, where <bdi> cannot go: the figure is wrapped in LRI…PDI instead, so
// «20%» does not turn into «%20» inside the Arabic sentence.
const promotionsHint = (promotions) => GRID_TEXT.promoTagHint(promotions
  .map(p => [promotionName(p.type, offersText), typeof p.percent === 'number' ? `\u2066${percent(p.percent)}\u2069` : null]
    .filter(Boolean).join(' '))
  .join('، '));

/**
 * A night under a booking bar stays unpainted; everything else takes its state's wash, and a
 * closed night (held, or sales closed) also gets the shared diagonal line.
 */
function cellStyle(state, covered) {
  const view = availabilityView(state);
  if (state === BOOKED) return covered ? undefined : { backgroundColor: view.bg, color: view.text };
  if (!view.cellBg) return undefined;
  return {
    backgroundColor: view.cellBg,
    color: view.text,
    backgroundImage: isClosedState(state) ? CLOSED_NIGHT_LINE : undefined
  };
}

function UnitRowView({ unit, dates, weekdays, dayLabels, today, nextStart, onOpen }) {
  const last = dates[dates.length - 1];
  const lanes = unit.bookings.reduce((n, b) => Math.max(n, b.lane + 1), 1);
  const sub = [unit.building, bedroomsLabel(unit.bedrooms), unit.owners[0]?.code].filter(Boolean).join(' · ');
  const promotions = runningPromotions(unit);

  return (
    <div
      className={`ag-row${lanes > 1 ? ' ag-row--stacked' : ''}`}
      style={lanes > 1 ? { '--ag-lanes': lanes } : undefined}
    >
      <button type="button" className="ag-unit" onClick={e => onOpen(e, { kind: 'unit', unitId: unit.id })}>
        <span className="ag-unit__code">
          {unit.code}
          {UNIT_STATUS_TAG[unit.status] && <span className="ag-tag">{UNIT_STATUS_TAG[unit.status]}</span>}
          {promotions.length > 0 && <span className="ag-tag" title={promotionsHint(promotions)}>{GRID_TEXT.promoTag}</span>}
        </span>
        {/* dir="auto": an English name truncates at ITS end ("Dev-Test 26 De…"), not at the
            start ("…t 26 Dec (2)") as it did when forced into the page's RTL. */}
        <span className="ag-unit__name" dir="auto">{unitTitle(unit)}</span>
        {sub && <span className="ag-unit__sub">{sub}</span>}
      </button>

      {unit.nights.map((night, i) => {
        const date = dates[i];
        const state = unit.states[i];
        const priced = (state === AVAILABLE || state === PARTIAL) && hasPrice(night.rate);
        const text = state === UNKNOWN ? '؟' : priced ? priceFigure(night.rate) : '';
        const cls = [
          'ag-cell',
          WEEKEND.has(weekdays[i]) && 'ag-cell--weekend',
          date === today && 'ag-cell--today',
          state === UNKNOWN && 'ag-cell--unknown',
          i > 0 && date.endsWith('-01') && 'ag-month-start'
        ].filter(Boolean).join(' ');
        return (
          <button
            type="button"
            key={date}
            className={cls}
            style={{ gridColumn: i + 2, ...cellStyle(state, night.bookingCount > 0) }}
            aria-label={[unit.code, dayLabels[i], availabilityView(state).label, priced ? formatSARCompact(night.rate) : null]
              .filter(Boolean).join(' — ')}
            onClick={e => onOpen(e, { kind: 'night', unitId: unit.id, index: i })}
          >
            {text}
          </button>
        );
      })}

      {unit.bookings.map(bar => {
        // Nights are [checkIn, checkOut): the departure day is not a night.
        const start = bar.checkIn < dates[0] ? 0 : dates.indexOf(bar.checkIn);
        const end = bar.checkOut > last ? dates.length : dates.indexOf(bar.checkOut);
        if (start < 0 || end <= start) return null;
        const mismatch = unit.nights.slice(start, end).some(n => n.mismatch);
        const platform = platformName(bar.platform);
        const guest = bar.guestName || GRID_TEXT.guestFallback;
        // The bar's colour already names the platform; the label only fits on 3+ nights, and on
        // a shorter bar it would crowd out the guest's name.
        const showPlatform = end - start >= 3;
        const cls = [
          'ag-bar',
          bar.checkIn < dates[0] && 'ag-bar--cut-start',
          bar.checkOut > nextStart && 'ag-bar--cut-end',
          mismatch && 'ag-bar--mismatch'
        ].filter(Boolean).join(' ');
        return (
          <button
            type="button"
            key={bar.id}
            className={cls}
            style={{ gridColumn: `${start + 2} / ${end + 2}`, '--ag-bar-color': getPlatformColor(bar.platform), '--ag-lane': bar.lane }}
            aria-label={[guest, platform, GRID_TEXT.bookingRange(formatShortDate(bar.checkIn), formatShortDate(bar.checkOut))].join(' — ')}
            onClick={e => onOpen(e, { kind: 'booking', unitId: unit.id, barId: bar.id })}
          >
            <span className="ag-bar__guest" dir="auto">{guest}</span>
            {showPlatform && <span className="ag-bar__platform">{platform}</span>}
          </button>
        );
      })}
    </div>
  );
}
const UnitRow = React.memo(UnitRowView);

// Focused as the popover mounts, so a keyboard user lands on its one action. (An effect cannot
// do it: the Overlay mounts its content a tick after `show` flips.)
const OpenButton = ({ onClick, children }) => (
  // eslint-disable-next-line jsx-a11y/no-autofocus
  <button type="button" className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} onClick={onClick} autoFocus>
    {children}
  </button>
);

/** Popover body — read from the CURRENT data by id, so a refetch never shows a stale copy. */
const PopContent = ({ pop, unit, dates, go, canOpenPricing }) => {
  if (pop.kind === 'unit') {
    const owner = unit.owners[0];
    return (
      <>
        <div className="ag-pop__title" id={POP_TITLE_ID}>{unit.code} · {unitTitle(unit)}</div>
        <dl className="ag-pop__facts">
          {unit.building && <><dt>{GRID_TEXT.buildingFact}</dt><dd>{unit.building}</dd></>}
          {bedroomsLabel(unit.bedrooms) && <><dt>{GRID_TEXT.bedroomsFact}</dt><dd>{bedroomsLabel(unit.bedrooms)}</dd></>}
          {owner && <><dt>{GRID_TEXT.owner}</dt><dd>{[owner.code, owner.name].filter(Boolean).join(' · ')}</dd></>}
        </dl>
        <OpenButton onClick={() => go(`/properties/${unit.id}`)}>{GRID_TEXT.openUnit}</OpenButton>
      </>
    );
  }

  if (pop.kind === 'booking') {
    const bar = unit.bookings.find(b => b.id === pop.barId);
    const start = bar.checkIn < dates[0] ? 0 : dates.indexOf(bar.checkIn);
    const end = bar.checkOut > dates[dates.length - 1] ? dates.length : dates.indexOf(bar.checkOut);
    const mismatch = unit.nights.slice(Math.max(start, 0), end).some(n => n.mismatch);
    return (
      <>
        <div className="ag-pop__title" id={POP_TITLE_ID}>{bar.guestName || GRID_TEXT.guestFallback}</div>
        <div className="ag-pop__muted">{unit.code} · {unitTitle(unit)}</div>
        {mismatch && <div className="ag-pop__warn">{GRID_TEXT.mismatch}</div>}
        <BookingRefLine confirmationCode={bar.confirmationCode} platform={bar.platform} bookingId={bar.id} />
        <dl className="ag-pop__facts">
          <dt>{GRID_TEXT.checkIn}</dt><dd>{formatDateWithDay(bar.checkIn)}</dd>
          <dt>{GRID_TEXT.checkOut}</dt><dd>{formatDateWithDay(bar.checkOut)}</dd>
          <dt>{GRID_TEXT.duration}</dt><dd>{nightsLabel(bar.nights)}</dd>
        </dl>
        <OpenButton onClick={() => go(`/bookings/${bar.id}`)}>{GRID_TEXT.openBooking}</OpenButton>
      </>
    );
  }

  const night = unit.nights[pop.index];
  const date = dates[pop.index];
  const view = availabilityView(unit.states[pop.index]);
  return (
    <>
      <div className="ag-pop__title" id={POP_TITLE_ID}>{unit.code} · {unitTitle(unit)}</div>
      <div className="ag-pop__muted">{formatDateWithDay(date)}</div>
      <span className="ag-pop__state" style={{ background: view.bg, color: view.text }}>{view.label}</span>
      <dl className="ag-pop__facts">
        <dt>{GRID_TEXT.publishedPrice}</dt>
        <dd>{hasPrice(night.rate) ? formatSARCompact(night.rate) : GRID_TEXT.noPrice}</dd>
      </dl>
      {night.blockReason === RELOCATION_HOLD && <div className="ag-pop__muted">{GRID_TEXT.relocation}</div>}
      {night.mismatch && <div className="ag-pop__warn">{GRID_TEXT.mismatch}</div>}
      {unit.airbnbOffers && (
        <NightAirbnbOffers offers={unit.airbnbOffers} date={date} unitId={unit.id} canOpenPricing={canOpenPricing} />
      )}
      <OpenButton onClick={() => go(`/properties/${unit.id}?tab=calendar&date=${date}`)}>
        {GRID_TEXT.openNight}
      </OpenButton>
    </>
  );
};

/** The popover's subject still exists in the data on screen? */
function resolvePop(pop, units, dates) {
  if (!pop) return null;
  const unit = units.find(u => u.id === pop.unitId);
  if (!unit) return null;
  if (pop.kind === 'booking' && !unit.bookings.some(b => b.id === pop.barId)) return null;
  if (pop.kind === 'night' && !dates[pop.index]) return null;
  return unit;
}

const AvailabilityGrid = ({ win, units, freeCounts, canOpenPricing = false }) => {
  const navigate = useNavigate();
  const boardRef = useRef(null);
  const [pop, setPop] = useState(null);
  const { dates, weekdays, today, nextStart } = win;
  // One formatted date per column, shared by every row's cell labels.
  const dayLabels = useMemo(() => dates.map(date => formatDateWithDay(date)), [dates]);
  const popUnit = resolvePop(pop, units, dates);

  const open = useCallback((event, subject) => setPop({ target: event.currentTarget, ...subject }), []);
  const go = (path) => {
    setPop(null);
    navigate(path);
  };

  // Outside-press and Escape close the popover. Presses inside the board are left to the cell
  // or bar that received them, so moving from one night to the next re-targets the popover
  // instead of closing it. `pointerdown` also fires for taps on iOS, where `mousedown` may not.
  useEffect(() => {
    if (!pop) return undefined;
    const onDown = (e) => {
      if (e.target.closest?.('.ag-pop') || boardRef.current?.contains(e.target)) return;
      setPop(null);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const trigger = pop.target;
      setPop(null);
      if (trigger?.isConnected) trigger.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pop]);

  // A refetch, a filter change or a width switch can remove what the popover points at —
  // close it rather than leave it anchored to a detached node or to data that is gone.
  useEffect(() => {
    if (pop && (!pop.target?.isConnected || !popUnit)) setPop(null);
  }, [pop, popUnit, units]);

  return (
    <>
      <div
        className="ag-board"
        ref={boardRef}
        role="region"
        aria-label={GRID_TEXT.boardLabel}
        style={{ '--ag-days': dates.length }}
      >
        <div className="ag-row ag-row--head">
          <div className="ag-unit">{GRID_TEXT.unitColumn}</div>
          {dates.map((date, i) => {
            const monthStart = date.endsWith('-01');
            const cls = [
              'ag-day',
              WEEKEND.has(weekdays[i]) && 'ag-day--weekend',
              date === today && 'ag-day--today',
              i > 0 && monthStart && 'ag-month-start'
            ].filter(Boolean).join(' ');
            return (
              <div key={date} className={cls} style={{ gridColumn: i + 2 }}>
                {(i === 0 || monthStart) && (
                  <span className="ag-day__month">{formatDate(date, { year: undefined, day: undefined })}</span>
                )}
                <span>{date === today ? GRID_TEXT.today : formatWeekdayName(weekdays[i])}</span>
                <span className="ag-day__num">{formatDayOfMonthLatn(date)}</span>
                <span className="ag-day__free">{GRID_TEXT.freeCount(formatCount(freeCounts[i]))}</span>
              </div>
            );
          })}
        </div>

        {units.map(unit => (
          <UnitRow
            key={unit.id}
            unit={unit}
            dates={dates}
            weekdays={weekdays}
            dayLabels={dayLabels}
            today={today}
            nextStart={nextStart}
            onOpen={open}
          />
        ))}
      </div>

      {/* No fade: the content is dropped the moment the popover closes, so a fading box would be empty. */}
      <Overlay target={pop?.target} show={Boolean(pop && popUnit)} placement="bottom" flip transition={false}>
        <Popover id="ag-popover" className="ag-pop" role="dialog" aria-labelledby={POP_TITLE_ID}>
          <Popover.Body>
            {pop && popUnit && <PopContent pop={pop} unit={popUnit} dates={dates} go={go} canOpenPricing={canOpenPricing} />}
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

export default AvailabilityGrid;
